/**
 * Explicit Excel note / threaded-comment tools for the ReAct executor.
 *
 * Read tools call the bridge host facade directly and always receive the
 * run's pinned workbook from `executorToolRuntime`. Write tools compile the
 * agent-friendly input into internal `CellAnnotationOperation` entries, then
 * delegate to `applyValidatedWorkbookModification` so plan gating, apply accounting,
 * undo, host readiness, and workbook targeting remain centralized.
 */

import type { ChartCapabilityToolDef } from "./chartCapabilityTools";
import {
  getAllComments,
  getAllNotes,
  getComment,
  getNote,
} from "./excelHostOps";
import type { RangeAddress } from "./services/factset/spreadsheetIntents";
import type { WorkbookModification } from "./types";
import type { ExecutorToolRuntimeContext } from "./executorToolRuntime";
import { applyValidatedWorkbookModification } from "./executorWriteTools";
import { isSingleCellAddress } from "./excelCellAddress";
import { isAbortError } from "../shared/utils/abortError";

const stepIdProperty = {
  type: ["string", "null"],
  description:
    "Existing run step id this mutation belongs to. Use the exact id from the current run steps.",
};

const sheetNameProperty = {
  type: "string",
  minLength: 1,
  description: "Resolved worksheet name from list_sheets.",
};

const addressProperty = {
  type: "string",
  minLength: 1,
  description: "One cell address in A1 notation, for example B5.",
};

const addressesProperty = {
  type: "array",
  minItems: 1,
  items: addressProperty,
  description: "One or more single-cell A1 addresses to delete in one batch.",
};

const textProperty = {
  type: "string",
  minLength: 1,
  description: "Annotation text. Whitespace-only text is invalid.",
};

const replaceExistingProperty = {
  type: "boolean",
  description:
    "Set true to replace an existing annotation. Set false to fail safely when one already exists.",
};

const setEntriesProperty = {
  type: "array",
  minItems: 1,
  description:
    "Annotations to set in one atomic workbook apply. Batch multiple cells here instead of calling the tool once per cell.",
  items: {
    type: "object",
    properties: {
      address: addressProperty,
      text: textProperty,
      replaceExisting: replaceExistingProperty,
    },
    required: ["address", "text", "replaceExisting"],
    additionalProperties: false,
  },
};

export const GET_COMMENT_TOOL: ChartCapabilityToolDef = {
  name: "get_comment",
  description:
    "Get threaded purple Excel comments from one cell in the pinned workbook. " +
    "Comments are not yellow sticky notes.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      sheetName: sheetNameProperty,
      address: addressProperty,
    },
    required: ["sheetName", "address"],
    additionalProperties: false,
  },
};

export const SET_COMMENT_TOOL: ChartCapabilityToolDef = {
  name: "set_comment",
  description:
    "Add threaded purple Excel comments to one worksheet in one atomic pinned-workbook apply. " +
    "Batch all target cells in entries; use replaceExisting=true only when replacement is intended.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      stepId: stepIdProperty,
      sheetName: sheetNameProperty,
      entries: setEntriesProperty,
    },
    required: ["stepId", "sheetName", "entries"],
    additionalProperties: false,
  },
};

export const DELETE_COMMENT_TOOL: ChartCapabilityToolDef = {
  name: "delete_comment",
  description:
    "Delete threaded purple Excel comments from one or more cells in the pinned workbook. " +
    "The operation is idempotent when a cell has no comment.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      stepId: stepIdProperty,
      sheetName: sheetNameProperty,
      addresses: addressesProperty,
    },
    required: ["stepId", "sheetName", "addresses"],
    additionalProperties: false,
  },
};

export const GET_ALL_COMMENTS_TOOL: ChartCapabilityToolDef = {
  name: "get_all_comments",
  description:
    "List threaded purple Excel comments on one explicitly selected worksheet in the pinned workbook. " +
    "For several sheets, emit independent calls together so the runtime can parallelize them safely.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: { sheetName: sheetNameProperty },
    required: ["sheetName"],
    additionalProperties: false,
  },
};

export const GET_NOTE_TOOL: ChartCapabilityToolDef = {
  name: "get_note",
  description:
    "Get the yellow sticky-note text from one cell in the pinned workbook. " +
    "Notes are not threaded purple comments.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      sheetName: sheetNameProperty,
      address: addressProperty,
    },
    required: ["sheetName", "address"],
    additionalProperties: false,
  },
};

export const SET_NOTE_TOOL: ChartCapabilityToolDef = {
  name: "set_note",
  description:
    "Add yellow sticky notes to one worksheet in one atomic pinned-workbook apply. " +
    "Batch all target cells in entries; use replaceExisting=true only when replacement is intended.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      stepId: stepIdProperty,
      sheetName: sheetNameProperty,
      entries: setEntriesProperty,
    },
    required: ["stepId", "sheetName", "entries"],
    additionalProperties: false,
  },
};

export const DELETE_NOTE_TOOL: ChartCapabilityToolDef = {
  name: "delete_note",
  description:
    "Delete yellow sticky notes from one or more cells in the pinned workbook. " +
    "The operation is idempotent when a cell has no note.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      stepId: stepIdProperty,
      sheetName: sheetNameProperty,
      addresses: addressesProperty,
    },
    required: ["stepId", "sheetName", "addresses"],
    additionalProperties: false,
  },
};

export const GET_ALL_NOTES_TOOL: ChartCapabilityToolDef = {
  name: "get_all_notes",
  description:
    "List yellow sticky notes on one explicitly selected worksheet in the pinned workbook. " +
    "For several sheets, emit independent calls together so the runtime can parallelize them safely.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: { sheetName: sheetNameProperty },
    required: ["sheetName"],
    additionalProperties: false,
  },
};

export const EXECUTOR_ANNOTATION_TOOLS: readonly ChartCapabilityToolDef[] = [
  GET_COMMENT_TOOL,
  SET_COMMENT_TOOL,
  DELETE_COMMENT_TOOL,
  GET_ALL_COMMENTS_TOOL,
  GET_NOTE_TOOL,
  SET_NOTE_TOOL,
  DELETE_NOTE_TOOL,
  GET_ALL_NOTES_TOOL,
];

interface CellInput {
  sheetName: string;
  address: string;
}

interface SetEntry {
  address: string;
  text: string;
  replaceExisting: boolean;
}

interface SetInput {
  stepId: string | null;
  sheetName: string;
  entries: SetEntry[];
}

interface DeleteInput {
  stepId: string | null;
  sheetName: string;
  addresses: string[];
}

interface GetAllInput {
  sheetName: string;
}

function asRecord(rawInput: unknown): Record<string, unknown> {
  return rawInput !== null &&
    typeof rawInput === "object" &&
    !Array.isArray(rawInput)
    ? (rawInput as Record<string, unknown>)
    : {};
}

function stringField(
  record: Record<string, unknown>,
  field: string,
): string {
  return typeof record[field] === "string"
    ? (record[field] as string).trim()
    : "";
}

function stepIdField(record: Record<string, unknown>): string | null {
  return stringField(record, "stepId") || null;
}

function normalizeCellInput(rawInput: unknown): CellInput {
  const record = asRecord(rawInput);
  return {
    sheetName: stringField(record, "sheetName"),
    address: stringField(record, "address"),
  };
}

function normalizeSetInput(rawInput: unknown): SetInput {
  const record = asRecord(rawInput);
  return {
    stepId: stepIdField(record),
    sheetName: stringField(record, "sheetName"),
    entries: Array.isArray(record.entries)
      ? record.entries.map((entry) => {
          const normalized = asRecord(entry);
          return {
            address: stringField(normalized, "address"),
            text:
              typeof normalized.text === "string"
                ? normalized.text
                : "",
            replaceExisting: normalized.replaceExisting === true,
          };
        })
      : [],
  };
}

function normalizeDeleteInput(rawInput: unknown): DeleteInput {
  const record = asRecord(rawInput);
  return {
    stepId: stepIdField(record),
    sheetName: stringField(record, "sheetName"),
    addresses: Array.isArray(record.addresses)
      ? record.addresses
          .filter((address): address is string => typeof address === "string")
          .map((address) => address.trim())
      : [],
  };
}

function normalizeGetAllInput(rawInput: unknown): GetAllInput {
  const record = asRecord(rawInput);
  return {
    sheetName: stringField(record, "sheetName"),
  };
}

function validateSheetName(sheetName: string): string | null {
  return sheetName ? null : "Missing required parameter: sheetName";
}

function validateCellAddress(address: string): string | null {
  if (!address) return "Missing required parameter: address";
  return isSingleCellAddress(address)
    ? null
    : `Invalid single-cell address: "${address}"`;
}

function validationError(error: string): string {
  return JSON.stringify({ ok: false, error });
}

function cellReference(
  input: CellInput,
  workbookName?: string,
): RangeAddress {
  return {
    address: input.address,
    referenceType: "absolute",
    worksheetName: input.sheetName,
    ...(workbookName ? { workbookName } : {}),
  };
}

function validateCellInput(input: CellInput): string | null {
  return validateSheetName(input.sheetName) || validateCellAddress(input.address);
}

function validateDeleteInput(input: DeleteInput): string | null {
  const sheetError = validateSheetName(input.sheetName);
  if (sheetError) return sheetError;
  if (input.addresses.length === 0) {
    return "Missing required parameter: addresses";
  }
  for (const address of input.addresses) {
    const addressError = validateCellAddress(address);
    if (addressError) return addressError;
  }
  return null;
}

function validateGetAllInput(input: GetAllInput): string | null {
  return validateSheetName(input.sheetName);
}

function validateSetInput(input: SetInput): string | null {
  const sheetError = validateSheetName(input.sheetName);
  if (sheetError) return sheetError;
  if (input.entries.length === 0) {
    return "Missing required parameter: entries";
  }
  for (const entry of input.entries) {
    const addressError = validateCellAddress(entry.address);
    if (addressError) return addressError;
    if (!entry.text.trim()) return "Missing required parameter: text";
  }
  return null;
}

export async function executeGetCommentTool(
  rawInput: unknown,
  workbookName?: string,
  signal?: AbortSignal,
): Promise<string> {
  const input = normalizeCellInput(rawInput);
  const error = validateCellInput(input);
  if (error) return validationError(error);
  try {
    const comments = await getComment(
      cellReference(input, workbookName),
      signal,
    );
    return JSON.stringify({
      ok: true,
      workbookName: workbookName ?? null,
      sheetName: input.sheetName,
      address: input.address,
      comments,
    });
  } catch (err) {
    if (isAbortError(err)) throw err;
    return validationError(
      `get_comment failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

export async function executeGetNoteTool(
  rawInput: unknown,
  workbookName?: string,
  signal?: AbortSignal,
): Promise<string> {
  const input = normalizeCellInput(rawInput);
  const error = validateCellInput(input);
  if (error) return validationError(error);
  try {
    const note = await getNote(cellReference(input, workbookName), signal);
    return JSON.stringify({
      ok: true,
      workbookName: workbookName ?? null,
      sheetName: input.sheetName,
      address: input.address,
      note,
      exists: note.length > 0,
    });
  } catch (err) {
    if (isAbortError(err)) throw err;
    return validationError(
      `get_note failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

export async function executeGetAllCommentsTool(
  rawInput: unknown,
  workbookName?: string,
  signal?: AbortSignal,
): Promise<string> {
  const input = normalizeGetAllInput(rawInput);
  const error = validateGetAllInput(input);
  if (error) return validationError(error);
  try {
    const comments = await getAllComments(
      {
        ...(workbookName ? { workbookName } : {}),
        worksheetNames: [input.sheetName],
      },
      signal,
    );
    return JSON.stringify({
      ok: true,
      workbookName: workbookName ?? null,
      sheetName: input.sheetName,
      comments: comments.map((comment) => ({
        ...comment,
        worksheetName: input.sheetName,
      })),
    });
  } catch (err) {
    if (isAbortError(err)) throw err;
    return validationError(
      `get_all_comments failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

export async function executeGetAllNotesTool(
  rawInput: unknown,
  workbookName?: string,
  signal?: AbortSignal,
): Promise<string> {
  const input = normalizeGetAllInput(rawInput);
  const error = validateGetAllInput(input);
  if (error) return validationError(error);
  try {
    const notes = await getAllNotes(
      {
        ...(workbookName ? { workbookName } : {}),
        worksheetNames: [input.sheetName],
      },
      signal,
    );
    return JSON.stringify({
      ok: true,
      workbookName: workbookName ?? null,
      sheetName: input.sheetName,
      notes: notes.map((note) => ({
        ...note,
        worksheetName: input.sheetName,
      })),
    });
  } catch (err) {
    if (isAbortError(err)) throw err;
    return validationError(
      `get_all_notes failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

async function executeSetAnnotationTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
  kind: "comment" | "note",
): Promise<string> {
  const input = normalizeSetInput(rawInput);
  const error = validateSetInput(input);
  if (error) return validationError(error);

  return applyValidatedWorkbookModification(ctx, {
    stepId: input.stepId,
    targetSheet: input.sheetName,
    modifications: {
      annotations: input.entries.map((entry) => ({
        kind,
        action: "set",
        sheetName: input.sheetName,
        address: entry.address,
        text: entry.text,
        replaceExisting: entry.replaceExisting,
      })),
      message: `Set ${input.entries.length} ${kind}${input.entries.length === 1 ? "" : "s"}`,
    } satisfies WorkbookModification,
  });
}

async function executeDeleteAnnotationTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
  kind: "comment" | "note",
): Promise<string> {
  const input = normalizeDeleteInput(rawInput);
  const error = validateDeleteInput(input);
  if (error) return validationError(error);

  return applyValidatedWorkbookModification(ctx, {
    stepId: input.stepId,
    targetSheet: input.sheetName,
    modifications: {
      annotations: [
        {
          kind,
          action: "delete",
          sheetName: input.sheetName,
          addresses: input.addresses,
        },
      ],
      message: `Deleted ${kind}${input.addresses.length === 1 ? "" : "s"} from ${input.sheetName}!${input.addresses.join(", ")}`,
    } satisfies WorkbookModification,
  });
}

export function executeSetCommentTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): Promise<string> {
  return executeSetAnnotationTool(ctx, rawInput, "comment");
}

export function executeDeleteCommentTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): Promise<string> {
  return executeDeleteAnnotationTool(ctx, rawInput, "comment");
}

export function executeSetNoteTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): Promise<string> {
  return executeSetAnnotationTool(ctx, rawInput, "note");
}

export function executeDeleteNoteTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): Promise<string> {
  return executeDeleteAnnotationTool(ctx, rawInput, "note");
}
